import type { ChapterData } from "./content-types";

/**
 * CAPÍTULO 1 — "El casco amarillo" (Obras del Norte).
 * Contenido portado del juego clásico y colocado en coordenadas de MUNDO
 * (x,y en tiles) para la sala caminable. Nada de esto es lógica: son datos.
 */
export const OBRAS: ChapterData = {
  id: "obras",
  title: "Obras del Norte",
  location: "obras",
  secStart: 40,
  secGreat: 78,
  secOk: 66,
  secBad: 58,

  interactables: [
    { id: "casco", type: "object", pos: { x: 3, y: 7 }, label: "Casco amarillo", verb: "inspect", ref: "casco", radius: 1.3 },
    { id: "escalera", type: "object", pos: { x: 4.5, y: 3 }, label: "Escalera", verb: "inspect", ref: "escalera", radius: 1.4 },
    { id: "herramienta", type: "object", pos: { x: 3, y: 5 }, label: "Herramienta caída", verb: "inspect", ref: "herramienta", radius: 1.3 },
    { id: "borde", type: "object", pos: { x: 6, y: 1.6 }, label: "Borde sin barandilla", verb: "inspect", ref: "borde", radius: 2.2 },
    { id: "material", type: "object", pos: { x: 8, y: 7 }, label: "Material en el paso", verb: "inspect", ref: "material", radius: 1.5 },
    { id: "registro", type: "document", pos: { x: 12.5, y: 4 }, label: "Registro", verb: "read", ref: "registro", radius: 1.4 },
    { id: "antonio", type: "npc", pos: { x: 8, y: 5 }, label: "Antonio", verb: "talk", ref: "antonio", radius: 1.5 },
    { id: "lucia", type: "npc", pos: { x: 10.5, y: 6.5 }, label: "Carmen", verb: "talk", ref: "lucia", radius: 1.5 },
    { id: "manolo", type: "npc", pos: { x: 12.5, y: 7 }, label: "Andrés", verb: "talk", ref: "manolo", radius: 1.5 },
    { id: "taquilla", type: "locker", pos: { x: 1.5, y: 8 }, label: "Taquilla de EPIs", verb: "open", radius: 1.6 },
    { id: "salida", type: "door", pos: { x: 12.5, y: 9 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    casco: {
      label: "Casco amarillo",
      text: "Un casco amarillo tirado en el suelo, junto a la base de la escalera. Tiene una <b>abolladura</b> reciente y la <b>cinta interior rota</b>.",
      note: { section: "objetos", heading: "Casco amarillo abollado", body: "Golpe reciente y cinta rota. Estaba en el suelo, no en una cabeza." },
      epiNote: { heading: "Casco de obra", body: "Sirve para lo que ha pasado aquí: un golpe en la cabeza." },
      comment: "Un casco en el suelo casi nunca es buena señal. Los cascos, en la cabeza.",
      signs: ["m_casco"],
    },
    escalera: {
      label: "Escalera",
      text: "Una escalera de mano <b>demasiado tumbada</b>, con la base <b>sin calzar</b> ni atar, y le falta un peldaño.",
      note: { section: "pistas", heading: "Escalera mal colocada", body: "Ángulo incorrecto, sin sujeción ni calce. Era el único acceso al nivel de arriba." },
      hazardId: "h_escalera",
      comment: "Esa escalera se sube... si te gustan las emociones fuertes. Ni atada, ni calzada.",
      signs: ["w_caida", "m_botas"],
      assess: {
        identify: {
          q: "¿Cuál es el riesgo principal de esta escalera?",
          options: [
            { text: "Caída a distinto nivel al ceder o resbalar la escalera", ok: true },
            { text: "Contacto eléctrico", ok: false },
            { text: "Sobreesfuerzo al transportarla", ok: false },
          ],
          hint: "Fíjate: tumbada, sin atar ni calzar y con un peldaño roto. Piensa qué pasa cuando alguien sube por ella.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Retirar la escalera dañada y montar un andamio/plataforma para ese acceso", tier: "eliminar", grade: "best", why: "El medio es inadecuado y está dañado (peldaño roto): sustituirlo por uno seguro elimina el riesgo de raíz. La jerarquía empieza por eliminar/sustituir." },
            { text: "Atarla arriba y calzar la base para que no se mueva", tier: "organizativa", grade: "ok", why: "Estabilizarla reduce el riesgo, pero con un peldaño roto la escalera sigue fuera de uso: lo correcto es sustituirla." },
            { text: "Que suba con cuidado sujetándose bien", tier: "ninguna", grade: "bad", why: "Confiar en el 'cuidado' ante un medio inseguro es la medida menos eficaz: no elimina nada.", danger: "A media altura el peldaño roto cede y la persona cae al vacío." },
          ],
        },
        apply: {
          intro: "Sustituye la escalera por un andamio seguro. Haz los pasos en orden.",
          steps: [
            "Señalizar la zona y retirar de servicio la escalera dañada",
            "Montar los módulos del andamio sobre una base nivelada",
            "Arriostrar el andamio y colocar los rodapiés",
            "Instalar la plataforma de trabajo con su barandilla",
          ],
          wrong: "Ese paso todavía no. Primero se retira lo inseguro y se monta desde la base, nivelando antes de subir.",
          success: "Andamio montado, nivelado y con barandilla. El acceso ya es seguro.",
        },
        note: { section: "epis", heading: "Escalera: eliminar antes que apañar", body: "Un medio de acceso dañado se retira y se sustituye por uno seguro (andamio/plataforma). Asegurar una escalera rota no es suficiente." },
      },
    },
    herramienta: {
      label: "Herramienta caída",
      text: "Una radial pequeña <b>caída justo debajo del borde abierto</b>. Como si se le hubiera escapado a alguien desde arriba.",
      note: { section: "objetos", heading: "Herramienta caída", body: "Cayó desde el nivel superior por el hueco sin proteger. Alguien trabajaba arriba." },
      hazardId: "h_herramienta",
      comment: "Esto ha caído desde arriba. Y por el hueco sin proteger.",
      signs: ["w_objetos", "m_casco"],
      assess: {
        identify: {
          q: "¿Qué riesgo revela esta herramienta caída?",
          options: [
            { text: "Caída de objetos desde el nivel superior por el hueco sin proteger", ok: true },
            { text: "Riesgo químico por el producto de la radial", ok: false },
            { text: "Ruido de la máquina", ok: false },
          ],
          hint: "No es solo que estorbe: ha caído por el hueco de arriba. Piensa qué protege a quien pasa por debajo.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Proteger el hueco (rodapié, entablado o red) para que no caigan objetos", tier: "colectiva", grade: "best", why: "Evitar la caída de objetos en su origen protege a la vez a todos los que pasan por debajo: protección colectiva, por encima del EPI." },
            { text: "Balizar la zona de abajo y poner cartel de 'caída de objetos'", tier: "organizativa", grade: "ok", why: "Señalizar avisa, pero no impide que las cosas caigan: es una medida complementaria, no la principal." },
            { text: "Que quien pase por debajo lleve casco y ya está", tier: "epi", grade: "bad", why: "El EPI es la última barrera, no la solución: no evita que el objeto caiga.", danger: "Cae un disco de radial desde tres metros; el casco amortigua un golpe pequeño, no ese impacto." },
          ],
        },
        apply: {
          intro: "Cierra el hueco para que no caigan objetos. Haz los pasos en orden.",
          steps: [
            "Desenchufar y retirar la radial de la zona de paso",
            "Cubrir el hueco con entablado resistente",
            "Colocar rodapié y red perimetral en el borde",
            "Señalizar 'riesgo de caída de objetos'",
          ],
          wrong: "Antes de señalizar hay que proteger de verdad: retira la herramienta y cierra el hueco primero.",
          success: "Hueco cubierto, perímetro protegido y señalizado. Ya no caerá nada por ahí.",
        },
        note: { section: "epis", heading: "Caída de objetos: proteger el hueco", body: "Ante caída de objetos, la protección colectiva (rodapié, red, entablado del hueco) va antes que el EPI. El casco es el último recurso." },
      },
    },
    borde: {
      label: "Borde sin barandilla",
      text: "El nivel superior tiene un <b>borde abierto a casi tres metros</b>. No hay barandilla ni rodapié. Se ven marcas de que <b>hubo</b> una barandilla aquí.",
      note: { section: "pistas", heading: "Borde sin protección", body: "Hueco a ~3 m sin barandilla. Había una y la quitaron. Riesgo de caída en altura." },
      hazardId: "h_borde",
      comment: "Aquí había una barandilla. Ahora hay un vacío y una vista estupenda. Mala combinación.",
      signs: ["w_caida", "m_arnes"],
      action: {
        q: "Hay un hueco a ~3 m sin barandilla y hay que trabajar cerca. ¿Qué haces?",
        options: [
          { text: "Reponer la barandilla y el rodapié antes de nada", ok: true, why: "Protección colectiva primero: la barandilla protege a todos, no solo a uno." },
          { text: "Trabajar con cuidado pegado a la pared", ok: false, why: "'Con cuidado' no sujeta a nadie. Un mal paso y es caída en altura." },
          { text: "Ponerte el arnés y asomarte a mirar", ok: false, why: "El arnés ayuda, pero sin punto de anclaje ni barandilla sigues expuesto (y los demás también)." },
        ],
        note: { section: "epis", heading: "Actuación: borde protegido", body: "Barandilla y rodapié repuestos antes de trabajar cerca del hueco. Protección colectiva." },
      },
    },
    material: {
      label: "Material en el paso",
      text: "Ferralla y sacos <b>en mitad de la zona de paso</b>. Para pasar hay que hacer eslalon, y hay restos de yeso resbaladizo.",
      note: { section: "pistas", heading: "Zona de paso invadida", body: "Material y suciedad donde la gente camina. Orden y limpieza inexistentes." },
      hazardId: "h_material",
      comment: "Orden y limpieza. Dos palabras que aquí no se han presentado todavía.",
      signs: ["w_suelo"],
      assess: {
        identify: {
          q: "¿Qué riesgo hay en esta zona de paso?",
          options: [
            { text: "Caídas al mismo nivel por obstáculos y suelo resbaladizo", ok: true },
            { text: "Riesgo eléctrico", ok: false },
            { text: "Atrapamiento entre piezas", ok: false },
          ],
          hint: "Mira el suelo: material en mitad del paso y restos de yeso. Piensa qué pasa al caminar con prisa o cargando algo.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Despejar el paso: retirar el material al acopio y limpiar el yeso", tier: "eliminar", grade: "best", why: "Quitar el obstáculo y limpiar elimina el riesgo en su origen. Orden y limpieza: lo primero de la jerarquía." },
            { text: "Delimitar con conos un pasillo alternativo", tier: "organizativa", grade: "ok", why: "Marcar un paso ayuda, pero el material sigue ahí estorbando: mejor retirarlo." },
            { text: "Avisar de que tengan cuidado al pasar", tier: "ninguna", grade: "bad", why: "El aviso verbal no despeja nada ni limpia el suelo: es la medida menos eficaz.", danger: "Alguien pasa con una carga, no ve el saco y tropieza contra la ferralla." },
          ],
        },
        apply: {
          intro: "Devuelve el paso al orden y la limpieza. Haz los pasos en orden.",
          steps: [
            "Delimitar la zona para trabajar sin cortar el paso",
            "Retirar la ferralla al acopio ordenado",
            "Retirar los sacos de la zona de circulación",
            "Limpiar los restos de yeso resbaladizo del suelo",
          ],
          wrong: "Ordena primero: delimita, retira lo pesado y después limpia. No al revés.",
          success: "Paso despejado y suelo limpio. Se circula sin tropiezos ni resbalones.",
        },
        note: { section: "epis", heading: "Orden y limpieza: eliminar el obstáculo", body: "Ante caídas al mismo nivel, retirar el material y limpiar el suelo (eliminar el riesgo) va antes que señalizar un paso alternativo." },
      },
    },
    registro: {
      label: "Registro / parte",
      text: 'El parte del accidente dice <b>"trabajador resbala y cae"</b>, sin hora, sin testigos, sin firma del responsable.',
      note: { section: "expedientes", heading: "Parte incompleto", body: "El parte cierra con 'resbaló y cayó'. Faltan hora, testigos y firma. Alguien lo cerró con prisa." },
      giveItem: "parte",
      comment: "'Resbaló y cayó.' Claro. Y la barandilla se fue sola de vacaciones.",
    },
  },

  npcs: {
    antonio: {
      name: "Antonio",
      role: "Veterano de la obra",
      portrait: "antonio",
      note: { section: "personas", heading: "Antonio", body: "Veterano. Lema: 'aquí siempre se ha hecho así'. Reconoce que quitaron la barandilla." },
      dialogue: [
        { speaker: "epi", text: "Buenas. ¿Tú viste lo que pasó?" },
        { speaker: "npc", text: "Uy, chico, un susto. Pero nada grave, cosas que pasan. Aquí llevamos toda la vida y nunca hubo problemas." },
        { choices: [ { text: "¿Y la barandilla del borde de arriba?", go: 3 }, { text: "¿'Cosas que pasan'? Alguien se ha caído.", go: 4 } ] },
        { speaker: "npc", text: "¿La barandilla? La quitamos ayer para meter unos sacos con la grúa. Iba a ponerla luego... pero si paras para eso no acabas nunca. Siempre lo hemos hecho así.", flag: "antonio_barandilla", note: { section: "pistas", heading: "La barandilla se retiró", body: "Antonio: quitaron la barandilla para pasar material y no la repusieron. 'Siempre se ha hecho así.'" }, end: true },
        { speaker: "npc", text: "Bueno... resbaló. Estaba arriba, en el borde. Le dije que bajara por la escalera con cuidado, que está floja.", flag: "antonio_escalera", end: true },
      ],
    },
    lucia: {
      name: "Carmen",
      role: "Trabajadora, muy observadora",
      portrait: "lucia",
      note: { section: "personas", heading: "Carmen", body: "Joven y observadora. Vio a un compañero subir al borde sin arnés y la escalera bailar." },
      dialogue: [
        { speaker: "npc", text: "Menos mal que vienes tú. Aquí todos miran para otro lado, como si no hubiera pasado nada." },
        { speaker: "epi", text: "A mí me lo puedes contar. ¿Qué viste exactamente?" },
        { speaker: "npc", text: "Estaba arriba, en el borde, con la radial. Sin arnés, ¿eh? Se agachó a por algo y se le fue de las manos." },
        { choices: [ { text: "¿Y por qué perdió el equilibrio?", go: 4 }, { text: "¿Por qué nadie llevaba arnés?", go: 5 } ] },
        { speaker: "npc", text: "Se le escapó la herramienta por el hueco y, al ir a cogerla por instinto, pisó el vacío. Un segundo. Si hubiera habido barandilla, ahora estaríamos hablando de un susto y ya.", flag: "lucia_arnes", note: { section: "pistas", heading: "Trabajo en altura sin arnés", body: "Carmen: el compañero estaba en el borde sin arnés; se le cayó la radial y, al ir a cogerla, perdió el equilibrio. Sin barandilla, la caída fue inevitable." }, end: true },
        { speaker: "npc", text: "Porque aquí el arnés 'molesta'. Y da igual: sin barandilla arriba no hay ni dónde engancharlo. Te mandan al borde y que la suerte haga el resto. Yo lo dije, ¿eh? Lo dije.", flag: "lucia_arnes", note: { section: "pistas", heading: "Trabajo en altura sin arnés", body: "Carmen: nadie usaba arnés y, sin barandilla, no había punto de anclaje. Avisó y no le hicieron caso." }, end: true },
      ],
    },
    manolo: {
      name: "Andrés",
      role: "Mantenimiento",
      portrait: "manolo",
      note: { section: "personas", heading: "Andrés", body: "Mantenimiento. Desmontó la barandilla y no la volvió a poner." },
      dialogue: [
        { speaker: "npc", text: "¿Vienes por lo de ayer? Mira, yo lo tengo clarísimo: fue mala suerte y punto." },
        { speaker: "epi", text: "La mala suerte no desmonta barandillas, Andrés. Alguien la quitó." },
        { choices: [ { text: "¿Quién retiró la barandilla del borde?", go: 3 }, { text: "Si estorbaba, ¿por qué no la repusiste?", go: 4 } ] },
        { speaker: "npc", text: "...vale. La quité yo. Con la grúa metiendo sacos, ahí en medio no había forma. Pensé: la pongo en cuanto acabe. Pero me llamaron a otra cosa y se me fue de la cabeza.", flag: "manolo_confiesa", note: { section: "pistas", heading: "Quién retiró la barandilla", body: "Andrés desmontó la barandilla para pasar material con la grúa y no la repuso: le surgió otra tarea. Nadie señalizó el hueco." }, end: true },
        { speaker: "npc", text: "Porque nadie repone nada hasta que pasa algo. 'Ya se pondrá', 'ahora no hay tiempo'... Así funciona esto. Y ni un cartel, ni una cinta avisando del hueco. Me como yo el marrón, pero la costumbre es de todos.", flag: "manolo_confiesa", note: { section: "pistas", heading: "Quién retiró la barandilla", body: "Andrés reconoce que quitó la barandilla y no se repuso ni se señalizó el hueco. Cultura de 'ya se pondrá' que nadie corrige." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_borde", category: "Caída en altura", severity: 3, discoverable: true, requiredAction: "Reponer barandilla y usar arnés", relatedEquipment: ["arnes"] },
    { id: "h_escalera", category: "Caída de personas", severity: 2, discoverable: true, requiredAction: "Retirar el medio dañado y sustituirlo por uno seguro (andamio/plataforma)" },
    { id: "h_material", category: "Orden y limpieza", severity: 1, discoverable: true, requiredAction: "Despejar la zona de paso" },
    { id: "h_herramienta", category: "Caída de objetos", severity: 2, discoverable: true, requiredAction: "Rodapié y retención de herramientas" },
  ],

  mission: {
    id: "cap1_casco_amarillo",
    title: "El casco amarillo",
    location: "obras",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con los trabajadores", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos de la zona", target: "3" },
      { id: "equip", type: "equip", label: "Prepárate en la taquilla", hidden: true },
      { id: "report", type: "report", label: "Sube a la zona y repón la barandilla", hidden: true },
    ],
  },

  epi: {
    needed: ["casco", "botas", "chaleco", "arnes"],
    optional: ["gafas", "guantes"],
    overkill: ["mascarilla", "auditiva"],
  },

  consequence: {
    title: "Entras a la zona de trabajo",
    lines: {
      casco: "Sin casco, un tornillo cae del andamio y te roza la cabeza. Esta vez solo el susto.",
      arnes: "Sin arnés, asomarte al borde a reponer la barandilla es justo el error que investigabas.",
      botas: "Sin botas, pisas un clavo saliente de un tablón. La suela normal no protege de eso.",
      chaleco: "Sin alta visibilidad, la grúa maniobra y el gruista casi no te ve.",
    },
    great: "Con el equipo puesto, reponer la barandilla y despejar el paso es cuestión de un rato. Trabajo hecho, sin sustos.",
    greatOver: "Eso sí: mascarilla y cascos antirruido no hacían falta aquí. “Como me ponga todo esto voy a parecer un Power Ranger de la prevención.”",
    ok: "Al final sale, pero te has llevado más de un susto que se podía evitar sabiendo lo que ya sabías.",
    bad: "Entrar así, después de investigar justo esto, es tentar a la suerte. Mejor equiparse bien.",
  },

  workId: "borde",
  reward: "tarjeta07",
  resolveNotes: [
    { section: "pistas", heading: "Tarjeta 07", body: "Estaba escondida dentro del casco amarillo. No aparece en ningún parte. ¿Qué es el 07?" },
    { section: "expedientes", heading: "Caso: El casco amarillo", body: "Accidente por trabajo en altura sin protección. Resuelto. Cabo suelto: la tarjeta 07." },
  ],
  resolveText:
    `<p>Con el equipo puesto, subes a reponer la <b>barandilla</b>, retiras la escalera dañada por un andamio seguro y despejas el paso. La obra respira.</p>
     <div class="epi-callout">Al recoger el casco amarillo, algo cae de dentro: una pequeña tarjeta con un número. <b>07</b>.<br>“Vale... esto no estaba en el parte del accidente.”</div>`,
};
